import type { BillingStatusResponse } from '@simplemon/lib';
import { format } from 'date-fns';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { billingApi } from '@/api/billing';
import { ManageBillingButton } from '@/components/billing/manage-billing-button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

function formatRenewalDate(dateIso: string | null): string {
  if (!dateIso) {
    return '—';
  }

  try {
    return format(new Date(dateIso), 'PPP');
  } catch {
    return '—';
  }
}

function getStatusBadge(
  status: BillingStatusResponse['subscriptionStatus'],
  hasActiveSubscription: boolean,
) {
  if (!status) {
    return { label: 'Not Subscribed', variant: 'outline' as const };
  }

  if (hasActiveSubscription) {
    return { label: status.replace(/_/g, ' '), variant: 'success' as const };
  }

  if (status === 'past_due') {
    return { label: 'Past Due', variant: 'warning' as const };
  }

  return { label: status.replace(/_/g, ' '), variant: 'outline' as const };
}

export function BillingPage() {
  const [status, setStatus] = useState<BillingStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const data = await billingApi.getStatus();
        setStatus(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load billing status';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    void loadStatus();
  }, []);

  const renewalDate = formatRenewalDate(status?.currentPeriodEnd ?? null);
  const badge = getStatusBadge(
    status?.subscriptionStatus ?? null,
    status?.hasActiveSubscription ?? false,
  );

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Billing</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage your Valvo.io subscription and view your billing status.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading billing status…
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Unable to load billing status</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : status ? (
        <Card>
          <CardHeader>
            <CardTitle>Subscription</CardTitle>
            <CardDescription>
              Your Stripe subscription controls access to monitoring beyond the free limits.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Status</p>
                <Badge variant={badge.variant} className="mt-1 capitalize">
                  {badge.label}
                </Badge>
              </div>
              {status.hasActiveSubscription && (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  Subscription is active
                </div>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Renews</p>
              <p className="mt-1 text-sm text-gray-900">{renewalDate}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Customer ID</p>
              <p className="mt-1 text-sm text-gray-900">
                {status.stripeCustomerId ?? 'Will be created during checkout'}
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-gray-500">
              You&apos;ll be redirected to Stripe Checkout or the Customer Portal to manage payment
              methods and invoices securely.
            </p>
            <ManageBillingButton mode={status.hasActiveSubscription ? 'manage' : 'subscribe'} />
          </CardFooter>
        </Card>
      ) : null}
    </div>
  );
}
